import { X } from 'lucide-react';
import { useState } from 'react';
import { CALENDAR_COLORS, PRIORITIES, TAGS, WEEKDAYS } from '../constants';
import type { CalendarEvent, EditableItem, Reminder, Todo } from '../types';
import { inputDate, parseInputDate, todayParts } from '../utils';

type SavePayload = Record<string, unknown>;

interface Props {
  editor: EditableItem;
  prefillDate: { year: number; month: number; day: number };
  busy: boolean;
  onClose: () => void;
  onSave: (payload: SavePayload) => Promise<void>;
}

export function EditorSheet(props: Props) {
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && props.onClose()}>
      <section className="editor-sheet" role="dialog" aria-modal="true" aria-label={props.editor.item ? '編輯' : '新增'}>
        <div className="sheet-handle" />
        <header className="sheet-header">
          <h2>{props.editor.item && !props.editor.duplicate ? '編輯' : '新增'}{props.editor.kind === 'todo' ? '待辦' : props.editor.kind === 'reminder' ? '提醒' : '事件'}</h2>
          <button className="icon-button" type="button" onClick={props.onClose} aria-label="關閉"><X size={22} /></button>
        </header>
        {props.editor.kind === 'todo' && <TodoForm item={props.editor.item} busy={props.busy} onClose={props.onClose} onSave={props.onSave} />}
        {props.editor.kind === 'reminder' && <ReminderForm item={props.editor.item} busy={props.busy} onClose={props.onClose} onSave={props.onSave} />}
        {props.editor.kind === 'calendar' && <CalendarForm item={props.editor.item} prefillDate={props.prefillDate} busy={props.busy} onClose={props.onClose} onSave={props.onSave} />}
      </section>
    </div>
  );
}

function TodoForm({ item, busy, onClose, onSave }: { item?: Todo; busy: boolean; onClose: () => void; onSave: Props['onSave'] }) {
  const [title, setTitle] = useState(item?.title ?? '');
  const [note, setNote] = useState(item?.note ?? '');
  const [tag, setTag] = useState(item?.tag ?? 'default');
  const [priority, setPriority] = useState(item?.priority ?? 'medium');
  return (
    <form className="editor-form" onSubmit={async (event) => {
      event.preventDefault();
      await onSave({ title: title.trim(), note, tag, priority });
    }}>
      <TextInput label="待辦事項" value={title} onChange={setTitle} required autoFocus />
      <TextArea label="備註" value={note} onChange={setNote} />
      <Choice label="分類" value={tag} onChange={(value) => setTag(value as Todo['tag'])} options={TAGS.map(({ value, label }) => ({ value, label }))} />
      <Choice label="優先級" value={priority} onChange={(value) => setPriority(value as Todo['priority'])} options={PRIORITIES.map(({ value, label }) => ({ value, label }))} />
      <FormActions busy={busy} valid={Boolean(title.trim())} onClose={onClose} />
    </form>
  );
}

function ReminderForm({ item, busy, onClose, onSave }: { item?: Reminder; busy: boolean; onClose: () => void; onSave: Props['onSave'] }) {
  const today = todayParts();
  const initialMode = item?.repeatType.startsWith('weekdays:') ? 'weekdays' : item?.repeatType ?? 'none';
  const [title, setTitle] = useState(item?.title ?? '');
  const [tag, setTag] = useState(item?.tag ?? 'default');
  const [repeatMode, setRepeatMode] = useState(initialMode);
  const [weekdays, setWeekdays] = useState<number[]>(item?.repeatType.startsWith('weekdays:') ? item.repeatType.slice(9).split(',').map(Number) : []);
  const [date, setDate] = useState(inputDate(item?.year ?? today.year, item?.month ?? today.month, item?.day ?? today.day));
  const [time, setTime] = useState(`${String(item?.hour ?? 9).padStart(2, '0')}:${String(item?.minute ?? 0).padStart(2, '0')}`);
  const [advanceMinutes, setAdvanceMinutes] = useState(item?.advanceMinutes ?? 0);
  const repeatOptions = [
    { value: 'none', label: '不重複' }, { value: 'daily', label: '每天' }, { value: 'weekly', label: '每週' },
    { value: 'monthly', label: '每月' }, { value: 'weekdays', label: '自定義' },
  ];
  const dateParts = parseInputDate(date);
  const [hour, minute] = time.split(':').map(Number);
  const valid = Boolean(title.trim()) && Number.isInteger(hour) && Number.isInteger(minute) && (repeatMode !== 'weekdays' || weekdays.length > 0);

  return (
    <form className="editor-form" onSubmit={async (event) => {
      event.preventDefault();
      if (!valid) return;
      await onSave({
        title: title.trim(), tag,
        repeatType: repeatMode === 'weekdays' ? `weekdays:${[...weekdays].sort().join(',')}` : repeatMode,
        year: dateParts.year, month: dateParts.month, day: dateParts.day,
        hour, minute, advanceMinutes,
      });
    }}>
      <TextInput label="提醒事項" value={title} onChange={setTitle} required autoFocus />
      <Choice label="分類" value={tag} onChange={(value) => setTag(value as Reminder['tag'])} options={TAGS.map(({ value, label }) => ({ value, label }))} />
      <Choice label="重複" value={repeatMode} onChange={setRepeatMode} options={repeatOptions} />
      {repeatMode === 'weekdays' && (
        <fieldset className="choice-field">
          <legend>選擇星期</legend>
          <div className="choice-row">
            {WEEKDAYS.map((day) => (
              <button
                type="button"
                key={day.value}
                className={weekdays.includes(day.value) ? 'selected' : ''}
                onClick={() => setWeekdays((current) => current.includes(day.value) ? current.filter((value) => value !== day.value) : [...current, day.value])}
              >週{day.label}</button>
            ))}
          </div>
          {!weekdays.length && <small className="form-error">至少選一天</small>}
        </fieldset>
      )}
      {repeatMode === 'none' && (
        <label className="field"><span>提醒日期</span><input type="date" value={date} onChange={(event) => setDate(event.target.value)} required /></label>
      )}
      <label className="field time-field"><span>提醒時間</span><input type="time" value={time} onChange={(event) => setTime(event.target.value)} required /></label>
      <Choice
        label="提前提醒"
        value={String(advanceMinutes)}
        onChange={(value) => setAdvanceMinutes(Number(value))}
        options={[{ value: '0', label: '準時' }, { value: '5', label: '5 分鐘' }, { value: '10', label: '10 分鐘' }, { value: '15', label: '15 分鐘' }, { value: '30', label: '30 分鐘' }, { value: '60', label: '1 小時' }]}
      />
      <FormActions busy={busy} valid={valid} onClose={onClose} />
    </form>
  );
}

function CalendarForm({ item, prefillDate, busy, onClose, onSave }: { item?: CalendarEvent; prefillDate: Props['prefillDate']; busy: boolean; onClose: () => void; onSave: Props['onSave'] }) {
  const startInitial = item ? inputDate(item.startYear, item.startMonth, item.startDay) : inputDate(prefillDate.year, prefillDate.month, prefillDate.day);
  const endInitial = item ? inputDate(item.endYear, item.endMonth, item.endDay) : startInitial;
  const [title, setTitle] = useState(item?.title ?? '');
  const [note, setNote] = useState(item?.note ?? '');
  const [startDate, setStartDate] = useState(startInitial);
  const [endDate, setEndDate] = useState(endInitial);
  const [tag, setTag] = useState(item?.tag ?? 'default');
  const [color, setColor] = useState(item?.color ?? 'blue');
  const valid = Boolean(title.trim()) && Boolean(startDate) && Boolean(endDate) && endDate >= startDate;
  return (
    <form className="editor-form" onSubmit={async (event) => {
      event.preventDefault();
      if (!valid) return;
      const start = parseInputDate(startDate);
      const end = parseInputDate(endDate);
      await onSave({
        title: title.trim(), note, tag, color,
        startYear: start.year, startMonth: start.month, startDay: start.day,
        endYear: end.year, endMonth: end.month, endDay: end.day,
      });
    }}>
      <TextInput label="事件名稱" value={title} onChange={setTitle} required autoFocus />
      <TextArea label="備註" value={note} onChange={setNote} />
      <div className="date-range-fields">
        <label className="field"><span>開始日期</span><input type="date" value={startDate} onChange={(event) => { setStartDate(event.target.value); if (event.target.value > endDate) setEndDate(event.target.value); }} required /></label>
        <label className="field"><span>結束日期</span><input type="date" value={endDate} min={startDate} onChange={(event) => setEndDate(event.target.value)} required /></label>
      </div>
      <Choice label="分類" value={tag} onChange={(value) => setTag(value as CalendarEvent['tag'])} options={TAGS.map(({ value, label }) => ({ value, label }))} />
      <fieldset className="choice-field">
        <legend>顏色</legend>
        <div className="color-choice-row">
          {CALENDAR_COLORS.map((option) => (
            <button type="button" key={option.value} className={color === option.value ? 'selected' : ''} onClick={() => setColor(option.value)} aria-label={option.label}>
              <span style={{ backgroundColor: option.color }} />
            </button>
          ))}
        </div>
      </fieldset>
      <FormActions busy={busy} valid={valid} onClose={onClose} />
    </form>
  );
}

function TextInput({ label, value, onChange, required, autoFocus }: { label: string; value: string; onChange: (value: string) => void; required?: boolean; autoFocus?: boolean }) {
  return <label className="field"><span>{label}</span><input value={value} onChange={(event) => onChange(event.target.value)} required={required} autoFocus={autoFocus} /></label>;
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="field"><span>{label}</span><textarea value={value} onChange={(event) => onChange(event.target.value)} rows={3} /></label>;
}

function Choice({ label, value, onChange, options }: { label: string; value: string; onChange: (value: string) => void; options: Array<{ value: string; label: string }> }) {
  return (
    <fieldset className="choice-field">
      <legend>{label}</legend>
      <div className="choice-row">
        {options.map((option) => <button type="button" key={option.value} className={value === option.value ? 'selected' : ''} onClick={() => onChange(option.value)}>{option.label}</button>)}
      </div>
    </fieldset>
  );
}

function FormActions({ busy, valid, onClose }: { busy: boolean; valid: boolean; onClose: () => void }) {
  return (
    <div className="form-actions">
      <button type="button" className="text-button" onClick={onClose}>取消</button>
      <button type="submit" className="primary-button" disabled={busy || !valid}>{busy ? '儲存中…' : '儲存'}</button>
    </div>
  );
}
